/**
 * Enhanced Educational Content - Uro2Go
 * 
 * This file contains supplementary educational content that integrates into
 * existing pages as collapsible subsections. Content is organized by topic
 * and can be accessed via search tags.
 * 
 * All content is written at 8th-grade reading level.
 * Both English and Spanish versions are provided.
 */

// ============================================
// PELVIC FLOOR PHYSICAL THERAPY CONTENT
// Integrates into: OAB, Incontinence, Chronic Prostatitis, Post-Prostatectomy
// ============================================

export const pelvicFloorPTContent = {
  // Generic overview - used as base for condition-specific versions
  overview: {
    en: {
      title: "Role of Pelvic Floor Physical Therapy",
      whatIsIt: "Pelvic floor physical therapy (Pelvic PT) is a special type of therapy that helps strengthen and relax the muscles in your pelvic area. These muscles support your bladder, bowel, and reproductive organs.",
      howItHelps: "A trained physical therapist teaches you exercises and techniques to improve muscle control. This can reduce symptoms like leakage, urgency, and pelvic pain.",
      whatToExpect: [
        "Your first visit takes about 45-60 minutes",
        "The therapist will ask about your symptoms and medical history",
        "They may examine your pelvic floor muscles (external or internal)",
        "You'll learn exercises to do at home",
        "Most people need 6-12 sessions over several weeks"
      ],
      whenToRequest: "Ask your urologist about pelvic PT if medications or lifestyle changes haven't fully helped your symptoms, or if you want to try therapy before or alongside other treatments.",
      faq: [
        {
          q: "Is pelvic floor therapy painful?",
          a: "No, pelvic PT should not be painful. You may feel some mild discomfort during internal exams, but therapy itself focuses on gentle exercises and relaxation techniques."
        },
        {
          q: "How long until I see results?",
          a: "Most people notice improvement within 4-6 weeks of consistent practice. Full benefits often take 2-3 months."
        },
        {
          q: "Do I need a referral?",
          a: "In most states, you can see a pelvic PT directly. However, insurance may require a referral from your doctor."
        },
        {
          q: "Is this just for women?",
          a: "No! Pelvic PT helps both men and women. Men benefit especially after prostate surgery or for chronic pelvic pain."
        }
      ]
    },
    es: {
      title: "Papel de la Fisioterapia del Suelo Pélvico",
      whatIsIt: "La fisioterapia del suelo pélvico es un tipo especial de terapia que ayuda a fortalecer y relajar los músculos de su área pélvica. Estos músculos sostienen su vejiga, intestino y órganos reproductivos.",
      howItHelps: "Un fisioterapeuta capacitado le enseña ejercicios y técnicas para mejorar el control muscular. Esto puede reducir síntomas como las fugas, la urgencia y el dolor pélvico.",
      whatToExpect: [
        "Su primera visita dura aproximadamente 45-60 minutos",
        "El terapeuta le preguntará sobre sus síntomas e historial médico",
        "Pueden examinar los músculos del suelo pélvico (externo o interno)",
        "Aprenderá ejercicios para hacer en casa",
        "La mayoría de las personas necesitan 6-12 sesiones durante varias semanas"
      ],
      whenToRequest: "Pregunte a su urólogo sobre la fisioterapia pélvica si los medicamentos o cambios de estilo de vida no han ayudado completamente sus síntomas.",
      faq: [
        {
          q: "¿Es dolorosa la terapia del suelo pélvico?",
          a: "No, la fisioterapia pélvica no debe ser dolorosa. Puede sentir alguna molestia leve durante los exámenes internos, pero la terapia se enfoca en ejercicios suaves y técnicas de relajación."
        },
        {
          q: "¿Cuánto tiempo hasta ver resultados?",
          a: "La mayoría de las personas notan mejoría dentro de 4-6 semanas de práctica constante. Los beneficios completos a menudo toman 2-3 meses."
        },
        {
          q: "¿Necesito una referencia?",
          a: "En la mayoría de los estados, puede ver a un fisioterapeuta pélvico directamente. Sin embargo, el seguro puede requerir una referencia de su médico."
        },
        {
          q: "¿Es esto solo para mujeres?",
          a: "¡No! La fisioterapia pélvica ayuda tanto a hombres como a mujeres. Los hombres se benefician especialmente después de la cirugía de próstata o para el dolor pélvico crónico."
        }
      ]
    }
  },

  // Condition-specific content
  conditions: {
    overactiveBladder: {
      en: {
        title: "Pelvic PT for Overactive Bladder",
        howItHelps: "Pelvic PT helps you regain control over your bladder by teaching you to relax overactive muscles and strengthen weak ones. This reduces sudden urges and frequent bathroom trips.",
        homeExercises: [
          {
            name: "Quick Flicks",
            description: "Quickly squeeze and release your pelvic muscles 10 times. This helps calm sudden urges.",
            frequency: "Do 3 sets daily, especially when you feel an urge"
          },
          {
            name: "Urge Suppression",
            description: "When you feel a sudden urge, stop moving, sit if possible, and do 5-10 quick pelvic squeezes. Wait for the urge to pass before walking to the bathroom.",
            frequency: "Use every time you feel a strong urge"
          },
          {
            name: "Bladder Training",
            description: "Gradually increase the time between bathroom trips. Start by waiting 5 extra minutes, then slowly increase.",
            frequency: "Daily practice"
          }
        ]
      },
      es: {
        title: "Fisioterapia Pélvica para Vejiga Hiperactiva",
        howItHelps: "La fisioterapia pélvica le ayuda a recuperar el control de su vejiga enseñándole a relajar los músculos hiperactivos y fortalecer los débiles.",
        homeExercises: [
          {
            name: "Contracciones Rápidas",
            description: "Apriete y suelte rápidamente sus músculos pélvicos 10 veces. Esto ayuda a calmar las urgencias repentinas.",
            frequency: "Haga 3 series diarias, especialmente cuando sienta urgencia"
          },
          {
            name: "Supresión de Urgencia",
            description: "Cuando sienta una urgencia repentina, deje de moverse, siéntese si es posible, y haga 5-10 contracciones pélvicas rápidas.",
            frequency: "Use cada vez que sienta una urgencia fuerte"
          },
          {
            name: "Entrenamiento de Vejiga",
            description: "Aumente gradualmente el tiempo entre visitas al baño. Comience esperando 5 minutos extra.",
            frequency: "Práctica diaria"
          }
        ]
      }
    },

    urinaryIncontinence: {
      en: {
        title: "Pelvic PT for Urinary Incontinence",
        howItHelps: "Pelvic PT strengthens the muscles that control urine flow. Stronger muscles mean better control and fewer accidents.",
        homeExercises: [
          {
            name: "Kegel Exercises",
            description: "Squeeze your pelvic muscles as if stopping urine flow. Hold for 5 seconds, then relax for 5 seconds.",
            frequency: "Do 10 repetitions, 3 times daily"
          },
          {
            name: "The Knack",
            description: "Before coughing, sneezing, or lifting, squeeze your pelvic muscles first. Hold during the activity.",
            frequency: "Every time before straining activities"
          },
          {
            name: "Bridge Exercise",
            description: "Lie on your back with knees bent. Squeeze your pelvic muscles and lift your hips. Hold 5 seconds.",
            frequency: "10 repetitions, twice daily"
          }
        ]
      },
      es: {
        title: "Fisioterapia Pélvica para Incontinencia Urinaria",
        howItHelps: "La fisioterapia pélvica fortalece los músculos que controlan el flujo de orina. Músculos más fuertes significan mejor control y menos accidentes.",
        homeExercises: [
          {
            name: "Ejercicios de Kegel",
            description: "Apriete los músculos pélvicos como si detuviera el flujo de orina. Mantenga 5 segundos, luego relaje 5 segundos.",
            frequency: "Haga 10 repeticiones, 3 veces al día"
          },
          {
            name: "La Técnica",
            description: "Antes de toser, estornudar o levantar algo, apriete primero los músculos pélvicos.",
            frequency: "Cada vez antes de actividades de esfuerzo"
          },
          {
            name: "Ejercicio de Puente",
            description: "Acuéstese boca arriba con las rodillas dobladas. Apriete los músculos pélvicos y levante las caderas.",
            frequency: "10 repeticiones, dos veces al día"
          }
        ]
      }
    },

    chronicProstatitis: {
      en: {
        title: "Pelvic PT for Chronic Prostatitis/Pelvic Pain",
        howItHelps: "For chronic pelvic pain, the focus is often on relaxing tight muscles rather than strengthening. Pelvic PT teaches techniques to release muscle tension and reduce pain.",
        homeExercises: [
          {
            name: "Diaphragmatic Breathing",
            description: "Breathe deeply into your belly, letting it expand. As you exhale, consciously relax your pelvic area.",
            frequency: "5-10 minutes, 3 times daily"
          },
          {
            name: "Pelvic Floor Drops",
            description: "Instead of squeezing, focus on completely relaxing and 'dropping' your pelvic floor muscles.",
            frequency: "10 drops, 3 times daily"
          },
          {
            name: "Hip Stretches",
            description: "Gentle stretches for hip flexors and inner thighs can relieve tension in the pelvic area.",
            frequency: "Hold each stretch 30 seconds, twice daily"
          }
        ]
      },
      es: {
        title: "Fisioterapia Pélvica para Prostatitis Crónica/Dolor Pélvico",
        howItHelps: "Para el dolor pélvico crónico, el enfoque suele ser relajar los músculos tensos en lugar de fortalecerlos. La fisioterapia pélvica enseña técnicas para liberar la tensión muscular.",
        homeExercises: [
          {
            name: "Respiración Diafragmática",
            description: "Respire profundamente hacia su vientre, dejándolo expandirse. Al exhalar, relaje conscientemente su área pélvica.",
            frequency: "5-10 minutos, 3 veces al día"
          },
          {
            name: "Relajación del Suelo Pélvico",
            description: "En lugar de apretar, enfóquese en relajar completamente y 'soltar' los músculos del suelo pélvico.",
            frequency: "10 relajaciones, 3 veces al día"
          },
          {
            name: "Estiramientos de Cadera",
            description: "Estiramientos suaves para los flexores de cadera pueden aliviar la tensión en el área pélvica.",
            frequency: "Mantenga cada estiramiento 30 segundos, dos veces al día"
          }
        ]
      }
    },

    postProstatectomy: {
      en: {
        title: "Pelvic PT After Prostate Surgery",
        howItHelps: "After prostate removal, the muscles that control urine may be weakened. Pelvic PT helps rebuild this control, often reducing recovery time significantly.",
        homeExercises: [
          {
            name: "Pre-Surgery Kegels",
            description: "If possible, start Kegel exercises before surgery. This helps you recover faster afterward.",
            frequency: "Start 2-4 weeks before surgery: 10 reps, 3 times daily"
          },
          {
            name: "Post-Surgery Recovery",
            description: "After catheter removal, begin gentle Kegels. Squeeze, hold 3-5 seconds, relax.",
            frequency: "Start with 5-10 reps, build up gradually"
          },
          {
            name: "Functional Training",
            description: "Practice squeezing before standing up, coughing, or lifting. This prevents leakage during activities.",
            frequency: "Every time before these activities"
          }
        ],
        timeline: "Most men see significant improvement within 3-6 months. Full recovery can take up to 12 months."
      },
      es: {
        title: "Fisioterapia Pélvica Después de Cirugía de Próstata",
        howItHelps: "Después de la extirpación de la próstata, los músculos que controlan la orina pueden debilitarse. La fisioterapia pélvica ayuda a reconstruir este control.",
        homeExercises: [
          {
            name: "Kegels Pre-Cirugía",
            description: "Si es posible, comience ejercicios de Kegel antes de la cirugía. Esto le ayuda a recuperarse más rápido.",
            frequency: "Comience 2-4 semanas antes: 10 repeticiones, 3 veces al día"
          },
          {
            name: "Recuperación Post-Cirugía",
            description: "Después de retirar el catéter, comience Kegels suaves. Apriete, mantenga 3-5 segundos, relaje.",
            frequency: "Comience con 5-10 repeticiones, aumente gradualmente"
          },
          {
            name: "Entrenamiento Funcional",
            description: "Practique apretar antes de levantarse, toser o levantar algo.",
            frequency: "Cada vez antes de estas actividades"
          }
        ],
        timeline: "La mayoría de los hombres ven mejoría significativa dentro de 3-6 meses. La recuperación completa puede tomar hasta 12 meses."
      }
    }
  }
};

// ============================================
// BCG THERAPY CONTENT
// Integrates into: Bladder Cancer section ONLY
// ============================================

export const bcgTherapyContent = {
  en: {
    title: "BCG Treatment Pathway",
    overview: "BCG (Bacillus Calmette-Guérin) is a type of immunotherapy used to treat non-muscle invasive bladder cancer. It stimulates your immune system to attack cancer cells in the bladder lining.",
    
    whoReceives: {
      title: "Who Receives BCG Treatment?",
      content: "BCG is typically recommended for:",
      items: [
        "High-grade non-muscle invasive bladder cancer (Ta high-grade, T1, CIS)",
        "Intermediate-risk bladder cancer to reduce recurrence",
        "After complete tumor removal (TURBT) to prevent cancer from coming back"
      ]
    },

    inductionVsMaintenance: {
      title: "Induction vs. Maintenance BCG",
      induction: {
        title: "Induction Phase",
        description: "Your initial series of BCG treatments",
        schedule: "Once weekly for 6 weeks",
        purpose: "Jump-starts your immune response against cancer cells"
      },
      maintenance: {
        title: "Maintenance Phase",
        description: "Ongoing treatments to keep cancer away",
        schedule: "Weekly treatments for 3 weeks at months 3, 6, 12, 18, 24, 30, and 36",
        purpose: "Maintains immune protection and reduces recurrence risk by up to 40%"
      }
    },

    treatmentDay: {
      title: "What to Expect on Treatment Day",
      beforeTreatment: [
        "Limit fluids for 4 hours before your appointment",
        "Empty your bladder right before the procedure",
        "Tell staff if you have fever, blood in urine, or UTI symptoms"
      ],
      duringTreatment: [
        "A small catheter is inserted into your bladder",
        "BCG solution is instilled (takes about 5 minutes)",
        "The catheter is removed",
        "You'll hold the medication in your bladder for 2 hours"
      ],
      afterTreatment: [
        "Change positions every 15 minutes to coat the entire bladder",
        "After 2 hours, urinate sitting down (to avoid splashing)",
        "Pour 2 cups of bleach into the toilet before flushing",
        "Wait 15 minutes, then flush twice",
        "Drink plenty of fluids for 24-48 hours to flush your system"
      ]
    },

    sideEffects: {
      title: "Common Side Effects",
      common: [
        "Burning or discomfort when urinating (most common)",
        "Needing to urinate frequently",
        "Low-grade fever or flu-like symptoms",
        "Fatigue",
        "Blood in urine"
      ],
      whenToCall: [
        "Fever above 101.5°F lasting more than 24 hours",
        "Severe pain that doesn't improve",
        "Unable to urinate",
        "Signs of infection spreading (joint pain, rash, persistent high fever)"
      ]
    },

    precautions: {
      title: "Post-Treatment Precautions",
      items: [
        "Do not share a toilet without bleaching first for 6 hours after treatment",
        "Avoid sexual contact for 48 hours after each treatment",
        "Use a condom for 1 week after treatment",
        "Wash hands thoroughly after urinating",
        "Wash any clothing or linens that contact urine separately"
      ]
    },

    followUp: {
      title: "Follow-Up Cystoscopy Schedule",
      schedule: [
        { timing: "3 months after completing induction", purpose: "Check treatment response" },
        { timing: "Every 3 months for year 1", purpose: "Early detection of recurrence" },
        { timing: "Every 6 months for year 2", purpose: "Ongoing surveillance" },
        { timing: "Yearly thereafter", purpose: "Long-term monitoring" }
      ],
      note: "Your doctor may adjust this schedule based on your specific situation."
    },

    checklist: {
      title: "BCG Treatment Day Checklist",
      items: [
        { item: "Limited fluids 4 hours before appointment", checked: false },
        { item: "Emptied bladder before procedure", checked: false },
        { item: "No fever or UTI symptoms", checked: false },
        { item: "Bleach available at home (2 cups per use)", checked: false },
        { item: "Plan to change positions during holding period", checked: false },
        { item: "Comfortable, loose clothing worn", checked: false },
        { item: "Partner aware of 48-hour precautions", checked: false }
      ]
    }
  },
  es: {
    title: "Vía de Tratamiento BCG",
    overview: "BCG (Bacilo de Calmette-Guérin) es un tipo de inmunoterapia usada para tratar el cáncer de vejiga no músculo invasivo. Estimula su sistema inmunológico para atacar las células cancerosas.",
    
    whoReceives: {
      title: "¿Quién Recibe Tratamiento BCG?",
      content: "BCG se recomienda típicamente para:",
      items: [
        "Cáncer de vejiga no músculo invasivo de alto grado (Ta alto grado, T1, CIS)",
        "Cáncer de vejiga de riesgo intermedio para reducir recurrencia",
        "Después de la resección completa del tumor (TURBT) para prevenir que el cáncer regrese"
      ]
    },

    inductionVsMaintenance: {
      title: "Inducción vs. Mantenimiento BCG",
      induction: {
        title: "Fase de Inducción",
        description: "Su serie inicial de tratamientos BCG",
        schedule: "Una vez por semana durante 6 semanas",
        purpose: "Inicia su respuesta inmune contra las células cancerosas"
      },
      maintenance: {
        title: "Fase de Mantenimiento",
        description: "Tratamientos continuos para mantener alejado el cáncer",
        schedule: "Tratamientos semanales por 3 semanas en los meses 3, 6, 12, 18, 24, 30 y 36",
        purpose: "Mantiene la protección inmune y reduce el riesgo de recurrencia hasta un 40%"
      }
    },

    treatmentDay: {
      title: "Qué Esperar el Día del Tratamiento",
      beforeTreatment: [
        "Limite los líquidos 4 horas antes de su cita",
        "Vacíe su vejiga justo antes del procedimiento",
        "Informe al personal si tiene fiebre, sangre en orina o síntomas de infección"
      ],
      duringTreatment: [
        "Se inserta un catéter pequeño en su vejiga",
        "Se instila la solución BCG (toma unos 5 minutos)",
        "Se retira el catéter",
        "Mantendrá el medicamento en su vejiga por 2 horas"
      ],
      afterTreatment: [
        "Cambie de posición cada 15 minutos para cubrir toda la vejiga",
        "Después de 2 horas, orine sentado (para evitar salpicaduras)",
        "Vierta 2 tazas de cloro en el inodoro antes de tirar",
        "Espere 15 minutos, luego tire dos veces",
        "Beba muchos líquidos por 24-48 horas para limpiar su sistema"
      ]
    },

    sideEffects: {
      title: "Efectos Secundarios Comunes",
      common: [
        "Ardor o molestia al orinar (más común)",
        "Necesidad de orinar frecuentemente",
        "Fiebre baja o síntomas gripales",
        "Fatiga",
        "Sangre en la orina"
      ],
      whenToCall: [
        "Fiebre superior a 101.5°F por más de 24 horas",
        "Dolor severo que no mejora",
        "Incapacidad para orinar",
        "Signos de infección propagándose (dolor articular, sarpullido, fiebre alta persistente)"
      ]
    },

    precautions: {
      title: "Precauciones Post-Tratamiento",
      items: [
        "No comparta un inodoro sin usar cloro primero por 6 horas después del tratamiento",
        "Evite contacto sexual por 48 horas después de cada tratamiento",
        "Use condón por 1 semana después del tratamiento",
        "Lávese las manos completamente después de orinar",
        "Lave cualquier ropa o sábanas que contacten orina por separado"
      ]
    },

    followUp: {
      title: "Programa de Cistoscopia de Seguimiento",
      schedule: [
        { timing: "3 meses después de completar la inducción", purpose: "Verificar respuesta al tratamiento" },
        { timing: "Cada 3 meses durante el año 1", purpose: "Detección temprana de recurrencia" },
        { timing: "Cada 6 meses durante el año 2", purpose: "Vigilancia continua" },
        { timing: "Anualmente después", purpose: "Monitoreo a largo plazo" }
      ],
      note: "Su médico puede ajustar este programa según su situación específica."
    },

    checklist: {
      title: "Lista de Verificación para el Día del Tratamiento BCG",
      items: [
        { item: "Líquidos limitados 4 horas antes de la cita", checked: false },
        { item: "Vejiga vaciada antes del procedimiento", checked: false },
        { item: "Sin fiebre o síntomas de infección", checked: false },
        { item: "Cloro disponible en casa (2 tazas por uso)", checked: false },
        { item: "Plan para cambiar posiciones durante el período de retención", checked: false },
        { item: "Ropa cómoda y suelta puesta", checked: false },
        { item: "Pareja informada de las precauciones de 48 horas", checked: false }
      ]
    }
  }
};

// ============================================
// CATHETER FLUSHING CONTENT
// Integrates into: Existing catheter care pages
// ============================================

export const catheterFlushingContent = {
  en: {
    title: "Catheter Flushing and Troubleshooting",
    overview: "Sometimes a catheter can become blocked by blood clots, sediment, or mucus. Flushing (also called irrigation) can help clear minor blockages. However, not all blockages should be flushed at home.",

    whenToFlush: {
      title: "When Flushing May Be Appropriate",
      items: [
        "Your doctor specifically instructed you to flush your catheter",
        "Urine flow has slowed but hasn't stopped completely",
        "You see small bits of sediment in the tubing",
        "Your doctor or nurse taught you the technique"
      ]
    },

    whenNotToFlush: {
      title: "When NOT to Flush",
      warning: true,
      items: [
        "No urine output at all — call your doctor",
        "You have severe pain",
        "You see fresh, bright red blood with clots",
        "You have never been taught how to flush",
        "Your catheter is a suprapubic catheter (unless specifically trained)",
        "You have signs of infection (fever, chills, cloudy foul-smelling urine)"
      ]
    },

    supplies: {
      title: "Supplies Needed",
      items: [
        "Sterile saline solution (0.9% sodium chloride)",
        "Catheter-tip syringe (usually 60mL)",
        "Clean gloves",
        "Alcohol wipes",
        "Clean towel or pad",
        "Container to collect fluid"
      ]
    },

    steps: {
      title: "Step-by-Step Flushing Instructions",
      steps: [
        {
          title: "Wash your hands thoroughly",
          detail: "Use soap and water for at least 20 seconds. Dry with a clean towel."
        },
        {
          title: "Gather supplies",
          detail: "Open packages but keep contents sterile. Put on clean gloves."
        },
        {
          title: "Prepare the syringe",
          detail: "Draw up 30-60mL of sterile saline into the syringe."
        },
        {
          title: "Disconnect the catheter",
          detail: "Wipe the connection point with alcohol. Disconnect the catheter from the drainage bag."
        },
        {
          title: "Insert the syringe",
          detail: "Gently insert the syringe tip into the catheter opening."
        },
        {
          title: "Flush gently",
          detail: "Push saline in SLOWLY and gently. Never force it. If you feel resistance, STOP."
        },
        {
          title: "Let fluid drain",
          detail: "Remove the syringe and let the fluid drain into a container. Check for clots or debris."
        },
        {
          title: "Repeat if needed",
          detail: "You may repeat 2-3 times. If still blocked, call your doctor."
        },
        {
          title: "Reconnect",
          detail: "Wipe connections with alcohol and reconnect to the drainage bag."
        }
      ]
    },

    troubleshooting: {
      title: "Troubleshooting Common Problems",
      problems: [
        {
          problem: "Urine is cloudy or smells bad",
          solution: "This may indicate infection. Increase fluids and contact your doctor."
        },
        {
          problem: "Catheter keeps blocking",
          solution: "You may need more fluids or a catheter change sooner than scheduled."
        },
        {
          problem: "Pain when flushing",
          solution: "STOP immediately. Pain means something is wrong. Call your doctor."
        },
        {
          problem: "Unable to push saline in",
          solution: "Don't force it. The blockage may be too severe for home flushing. Seek medical help."
        }
      ]
    },

    warningSignsEmergency: {
      title: "Warning Signs — Seek Medical Help",
      emergency: true,
      items: [
        "No urine output for 4+ hours despite flushing",
        "Severe abdominal or bladder pain",
        "Fever over 101°F (38.3°C)",
        "Catheter has fallen out",
        "Bright red blood filling the bag",
        "Signs of sepsis: confusion, rapid heartbeat, feeling very ill"
      ]
    }
  },
  es: {
    title: "Lavado de Catéter y Solución de Problemas",
    overview: "A veces un catéter puede bloquearse con coágulos de sangre, sedimentos o moco. El lavado (también llamado irrigación) puede ayudar a limpiar bloqueos menores. Sin embargo, no todos los bloqueos deben lavarse en casa.",
    
    whenToFlush: {
      title: "Cuándo el Lavado Puede Ser Apropiado",
      items: [
        "Su médico le indicó específicamente lavar su catéter",
        "El flujo de orina se ha reducido pero no se ha detenido completamente",
        "Ve pequeñas partículas de sedimento en el tubo",
        "Su médico o enfermera le enseñó la técnica"
      ]
    },

    whenNotToFlush: {
      title: "Cuándo NO Lavar",
      warning: true,
      items: [
        "Sin salida de orina — llame a su médico",
        "Tiene dolor severo",
        "Ve sangre roja brillante con coágulos",
        "Nunca le enseñaron cómo lavar",
        "Su catéter es suprapúbico (a menos que esté específicamente entrenado)",
        "Tiene signos de infección (fiebre, escalofríos, orina turbia maloliente)"
      ]
    },

    supplies: {
      title: "Suministros Necesarios",
      items: [
        "Solución salina estéril (cloruro de sodio 0.9%)",
        "Jeringa con punta de catéter (generalmente 60mL)",
        "Guantes limpios",
        "Toallitas con alcohol",
        "Toalla o almohadilla limpia",
        "Recipiente para recolectar líquido"
      ]
    },

    steps: {
      title: "Instrucciones Paso a Paso para el Lavado",
      steps: [
        {
          title: "Lávese las manos completamente",
          detail: "Use jabón y agua por al menos 20 segundos. Seque con una toalla limpia."
        },
        {
          title: "Reúna los suministros",
          detail: "Abra los paquetes pero mantenga el contenido estéril. Póngase guantes limpios."
        },
        {
          title: "Prepare la jeringa",
          detail: "Extraiga 30-60mL de solución salina estéril en la jeringa."
        },
        {
          title: "Desconecte el catéter",
          detail: "Limpie el punto de conexión con alcohol. Desconecte el catéter de la bolsa de drenaje."
        },
        {
          title: "Inserte la jeringa",
          detail: "Inserte suavemente la punta de la jeringa en la abertura del catéter."
        },
        {
          title: "Lave suavemente",
          detail: "Empuje la solución LENTAMENTE y suavemente. Nunca la fuerce. Si siente resistencia, PARE."
        },
        {
          title: "Deje drenar el líquido",
          detail: "Retire la jeringa y deje que el líquido drene en un recipiente. Revise si hay coágulos o residuos."
        },
        {
          title: "Repita si es necesario",
          detail: "Puede repetir 2-3 veces. Si aún está bloqueado, llame a su médico."
        },
        {
          title: "Reconecte",
          detail: "Limpie las conexiones con alcohol y reconecte a la bolsa de drenaje."
        }
      ]
    },

    troubleshooting: {
      title: "Solución de Problemas Comunes",
      problems: [
        {
          problem: "La orina está turbia o huele mal",
          solution: "Esto puede indicar infección. Aumente los líquidos y contacte a su médico."
        },
        {
          problem: "El catéter sigue bloqueándose",
          solution: "Puede necesitar más líquidos o un cambio de catéter antes de lo programado."
        },
        {
          problem: "Dolor al lavar",
          solution: "PARE inmediatamente. El dolor significa que algo está mal. Llame a su médico."
        },
        {
          problem: "No puede empujar la solución salina",
          solution: "No la fuerce. El bloqueo puede ser demasiado severo para lavado en casa. Busque ayuda médica."
        }
      ]
    },

    warningSignsEmergency: {
      title: "Señales de Advertencia — Busque Ayuda Médica",
      emergency: true,
      items: [
        "Sin salida de orina por más de 4 horas a pesar del lavado",
        "Dolor severo abdominal o de vejiga",
        "Fiebre superior a 101°F (38.3°C)",
        "El catéter se ha salido",
        "Sangre roja brillante llenando la bolsa",
        "Signos de sepsis: confusión, latido cardíaco rápido, sentirse muy mal"
      ]
    }
  }
};

// ============================================
// NEPHROSTOMY TUBE CARE CONTENT
// Integrates into: Kidney Stone and Obstruction pages
// ============================================

export const nephrostomyContent = {
  en: {
    title: "Nephrostomy Tube Care at Home",
    whatIsIt: "A nephrostomy tube is a thin, flexible tube placed through your skin directly into your kidney. It drains urine when the normal pathway (ureter) is blocked. The tube connects to a drainage bag outside your body.",

    whyNeeded: {
      title: "Why You May Need a Nephrostomy",
      items: [
        "Kidney stone causing blockage",
        "Tumor or scar tissue blocking the ureter",
        "Infection that needs to drain",
        "After certain kidney surgeries",
        "To protect the kidney while other treatments are planned"
      ]
    },

    dailyCare: {
      title: "Daily Care Steps",
      steps: [
        {
          title: "Check the tube and bag",
          detail: "Look at the insertion site, tubing, and bag every day. Make sure urine is draining."
        },
        {
          title: "Keep the area clean and dry",
          detail: "Gently clean around the tube daily with mild soap and water. Pat dry completely."
        },
        {
          title: "Secure the tube",
          detail: "Tape the tube to your skin to prevent pulling. Change tape if it gets wet or loose."
        },
        {
          title: "Keep the bag below kidney level",
          detail: "This allows urine to drain properly by gravity. Don't let it drag on the floor."
        },
        {
          title: "Empty the bag regularly",
          detail: "Empty when half to two-thirds full. Record the amount if instructed."
        }
      ]
    },

    dressingChanges: {
      title: "Dressing Changes",
      frequency: "Change the dressing every 2-3 days, or sooner if wet, dirty, or loose.",
      supplies: [
        "Sterile gauze pads (4x4)",
        "Medical tape or transparent dressing",
        "Clean gloves",
        "Sterile saline or wound cleanser",
        "Cotton-tipped swabs"
      ],
      steps: [
        "Wash hands and put on clean gloves",
        "Carefully remove old dressing",
        "Clean around the tube site using circular motions outward",
        "Let the area air dry or pat with sterile gauze",
        "Apply clean gauze around the tube",
        "Secure with tape or transparent dressing",
        "Make sure the tube is not kinked"
      ]
    },

    bagEmptying: {
      title: "Emptying the Drainage Bag",
      steps: [
        "Wash your hands",
        "Hold the drainage spout over a toilet or measuring container",
        "Open the spout and let urine drain completely",
        "Clean the spout with an alcohol wipe",
        "Close the spout securely",
        "Wash your hands again"
      ],
      note: "Record the amount of urine if your doctor asked you to track output."
    },

    flushing: {
      title: "Tube Flushing (If Prescribed)",
      note: "Only flush if your doctor specifically instructed you to do so.",
      steps: [
        "Gather supplies: sterile saline, 10mL syringe, alcohol wipes, gloves",
        "Wash hands and put on gloves",
        "Disconnect tube from bag, keeping both ends clean",
        "Slowly push 5-10mL saline into the tube",
        "Let it drain back out by gravity",
        "Reconnect to drainage bag",
        "Wash hands"
      ]
    },

    redFlags: {
      title: "Warning Signs — Call Your Doctor",
      warning: true,
      items: [
        "Fever over 100.4°F (38°C)",
        "Increasing pain at the tube site",
        "Redness, swelling, or pus around the tube",
        "Cloudy, foul-smelling, or bloody urine",
        "No urine draining for several hours",
        "Tube falls out or moves significantly",
        "Leaking around the tube site"
      ]
    },

    emergencySigns: {
      title: "Seek Emergency Care If",
      emergency: true,
      items: [
        "Tube falls out completely",
        "Heavy bleeding from the tube site",
        "Fever with chills and shaking",
        "Severe pain not relieved by medication",
        "Feeling very confused or unwell"
      ]
    },

    exchangeTimeline: {
      title: "Tube Exchange Schedule",
      content: "Nephrostomy tubes typically need to be exchanged every 2-3 months. Your doctor will schedule this. The exchange is usually done in a radiology department and takes about 30-60 minutes.",
      note: "Never try to replace the tube yourself. If it falls out, cover the site with clean gauze and contact your doctor immediately."
    }
  },
  es: {
    title: "Cuidado del Tubo de Nefrostomía en Casa",
    whatIsIt: "Un tubo de nefrostomía es un tubo delgado y flexible colocado a través de su piel directamente en su riñón. Drena orina cuando el camino normal (uréter) está bloqueado. El tubo se conecta a una bolsa de drenaje fuera de su cuerpo.",

    whyNeeded: {
      title: "Por Qué Puede Necesitar una Nefrostomía",
      items: [
        "Cálculo renal causando bloqueo",
        "Tumor o tejido cicatricial bloqueando el uréter",
        "Infección que necesita drenarse",
        "Después de ciertas cirugías de riñón",
        "Para proteger el riñón mientras se planean otros tratamientos"
      ]
    },

    dailyCare: {
      title: "Pasos de Cuidado Diario",
      steps: [
        {
          title: "Revise el tubo y la bolsa",
          detail: "Mire el sitio de inserción, el tubo y la bolsa todos los días. Asegúrese de que la orina esté drenando."
        },
        {
          title: "Mantenga el área limpia y seca",
          detail: "Limpie suavemente alrededor del tubo diariamente con jabón suave y agua. Seque completamente."
        },
        {
          title: "Asegure el tubo",
          detail: "Pegue el tubo a su piel para evitar que se jale. Cambie la cinta si se moja o afloja."
        },
        {
          title: "Mantenga la bolsa debajo del nivel del riñón",
          detail: "Esto permite que la orina drene correctamente por gravedad. No la deje arrastrar en el piso."
        },
        {
          title: "Vacíe la bolsa regularmente",
          detail: "Vacíe cuando esté medio a dos tercios llena. Registre la cantidad si se le indicó."
        }
      ]
    },

    dressingChanges: {
      title: "Cambios de Vendaje",
      frequency: "Cambie el vendaje cada 2-3 días, o antes si está mojado, sucio o suelto.",
      supplies: [
        "Gasas estériles (4x4)",
        "Cinta médica o vendaje transparente",
        "Guantes limpios",
        "Solución salina estéril o limpiador de heridas",
        "Hisopos de algodón"
      ],
      steps: [
        "Lávese las manos y póngase guantes limpios",
        "Retire cuidadosamente el vendaje viejo",
        "Limpie alrededor del sitio del tubo usando movimientos circulares hacia afuera",
        "Deje secar el área al aire o seque con gasa estéril",
        "Aplique gasa limpia alrededor del tubo",
        "Asegure con cinta o vendaje transparente",
        "Asegúrese de que el tubo no esté doblado"
      ]
    },

    bagEmptying: {
      title: "Vaciado de la Bolsa de Drenaje",
      steps: [
        "Lávese las manos",
        "Sostenga el pico de drenaje sobre un inodoro o recipiente de medición",
        "Abra el pico y deje que la orina drene completamente",
        "Limpie el pico con una toallita con alcohol",
        "Cierre el pico de forma segura",
        "Lávese las manos nuevamente"
      ],
      note: "Registre la cantidad de orina si su médico le pidió que lleve un seguimiento."
    },

    flushing: {
      title: "Lavado del Tubo (Si Está Prescrito)",
      note: "Solo lave si su médico le indicó específicamente hacerlo.",
      steps: [
        "Reúna suministros: solución salina estéril, jeringa de 10mL, toallitas con alcohol, guantes",
        "Lávese las manos y póngase guantes",
        "Desconecte el tubo de la bolsa, manteniendo ambos extremos limpios",
        "Empuje lentamente 5-10mL de solución salina en el tubo",
        "Déjelo drenar por gravedad",
        "Reconecte a la bolsa de drenaje",
        "Lávese las manos"
      ]
    },

    redFlags: {
      title: "Señales de Advertencia — Llame a Su Médico",
      warning: true,
      items: [
        "Fiebre superior a 100.4°F (38°C)",
        "Dolor creciente en el sitio del tubo",
        "Enrojecimiento, hinchazón o pus alrededor del tubo",
        "Orina turbia, maloliente o con sangre",
        "Sin drenaje de orina por varias horas",
        "El tubo se cae o se mueve significativamente",
        "Fugas alrededor del sitio del tubo"
      ]
    },

    emergencySigns: {
      title: "Busque Atención de Emergencia Si",
      emergency: true,
      items: [
        "El tubo se cae completamente",
        "Sangrado abundante del sitio del tubo",
        "Fiebre con escalofríos y temblores",
        "Dolor severo que no mejora con medicamentos",
        "Sentirse muy confundido o mal"
      ]
    },

    exchangeTimeline: {
      title: "Programa de Intercambio del Tubo",
      content: "Los tubos de nefrostomía típicamente necesitan intercambiarse cada 2-3 meses. Su médico programará esto. El intercambio generalmente se hace en un departamento de radiología y toma aproximadamente 30-60 minutos.",
      note: "Nunca intente reemplazar el tubo usted mismo. Si se cae, cubra el sitio con gasa limpia y contacte a su médico inmediatamente."
    }
  }
};

// ============================================
// SEARCH TAGS FOR CONTENT DISCOVERY
// ============================================

export const contentSearchTags = {
  pelvicPT: [
    "pelvic pt", "pelvic floor", "physical therapy", "kegel", "incontinence",
    "overactive bladder", "oab", "pelvic pain", "prostatitis", "prostatectomy",
    "fisioterapia pélvica", "suelo pélvico"
  ],
  bcg: [
    "bcg", "bcg therapy", "bladder cancer", "immunotherapy", "intravesical",
    "maintenance bcg", "induction bcg", "turbt", "terapia bcg", "cáncer de vejiga"
  ],
  catheterFlushing: [
    "catheter", "flush", "flushing", "irrigation", "blocked catheter",
    "catheter care", "foley", "catéter", "lavado"
  ],
  nephrostomy: [
    "nephrostomy", "kidney tube", "neph tube", "pcn", "drainage tube",
    "kidney drain", "obstruction", "nefrostomía", "tubo renal"
  ]
};

export default {
  pelvicFloorPTContent,
  bcgTherapyContent,
  catheterFlushingContent,
  nephrostomyContent,
  contentSearchTags
};
